import { DOM } from './state.js';
import { FaqStaffShell, FaqStaffResultados } from './components/faq/FaqStaffView.js';
import { FaqAdminView } from './components/faq/FaqAdminView.js';
import { FaqBusquedasView } from './components/faq/FaqBusquedasView.js';
import { showToast } from './components/shared/Toast.js';
import { loadEntradasVisibles, registrarBusqueda } from './services/staff-faq.service.js';
import {
    loadEntradas,
    guardarEntrada,
    cambiarVisibilidad,
    eliminarEntrada,
    loadBusquedasPendientes,
    marcarRevisadas,
} from './services/admin-faq.service.js';
import { buscar, claveBusqueda, esConsultaRegistrable } from './shared/faq-search-policy.js';

/**
 * Staff FAQ sections (openspec/changes/staff-faq/spec.md): staff "Preguntas"
 * (`faq`), admin "FAQ" (`faq-admin`) and admin "Búsquedas" (`faq-busquedas`).
 * Registered in `SECTION_REGISTRY` (js/admin.js), same shape as Fichaje
 * (js/kiosco.js): each `mountFaq*Section(mount)` builds its own mount node
 * inside the panel's content mount and owns its state and listeners.
 *
 * Every async result checks its section's `seq` before painting or toasting:
 * each section's teardown (`stopFaq*`, run on tab change and logout) and each
 * new mount bump it, so an in-flight response becomes stale.
 */

/** A finished search: the employee stopped typing this long (spec B3). */
export const FAQ_DEBOUNCE_MS = 1500;

// ---------------------------------------------------------------------------
// Staff: Preguntas
// ---------------------------------------------------------------------------

const staff = {
    seq: 0,
    uiState: /** @type {{ status: 'loading' | 'error' | 'ready', mensaje?: string }} */ ({ status: 'loading' }),
    entradas: /** @type {Array<Record<string, any>>} */ ([]),
    consulta: '',
    abiertas: new Set(),
    /** Exact (trimmed) query already reported with the button (B4): disabled until the query changes. */
    consultaReportada: /** @type {string | null} */ (null),
    /** Exact (trimmed) query whose report is in flight: disabled across redraws, never sent twice. */
    consultaReportando: /** @type {string | null} */ (null),
    /** Key of the last query recorded as `sin_resultado` in this mount (B3: not twice in a row). */
    registradaClave: /** @type {string | null} */ (null),
    timer: /** @type {ReturnType<typeof setTimeout> | null} */ (null),
};

const getStaffResultados = () => DOM.adminContainer?.querySelector('#faq-staff-resultados') ?? null;
const getStaffInput = () => DOM.adminContainer?.querySelector('input[data-faq-buscar]') ?? null;

/**
 * Entry point of the staff "Preguntas" tab (`SECTION_REGISTRY.faq.mount`).
 * @param {HTMLElement | null} mount
 */
export function mountFaqStaffSection(mount) {
    if (!mount) return;
    stopFaqStaff();
    staff.uiState = { status: 'loading' };
    staff.entradas = [];
    staff.consulta = '';
    staff.abiertas = new Set();
    staff.consultaReportada = null;
    staff.consultaReportando = null;
    staff.registradaClave = null;

    mount.innerHTML = FaqStaffShell();
    const input = getStaffInput();
    input?.addEventListener('input', handleStaffInput);
    input?.addEventListener('keydown', handleStaffKeydown);
    getStaffResultados()?.addEventListener('click', handleStaffClick);
    input?.focus();

    renderStaffResultados();
    void cargarStaff();
}

/**
 * Teardown when leaving the tab or logging out (`SECTION_REGISTRY.faq.teardown`):
 * a pending "finished search" must not be recorded after the employee left (B3).
 */
export function stopFaqStaff() {
    if (staff.timer) clearTimeout(staff.timer);
    staff.timer = null;
    staff.seq += 1;
}

async function cargarStaff() {
    const seq = staff.seq;
    staff.uiState = { status: 'loading' };
    renderStaffResultados();
    const res = await loadEntradasVisibles();
    if (seq !== staff.seq) return;
    if (res.ok) {
        staff.entradas = res.entradas;
        staff.uiState = { status: 'ready' };
    } else {
        staff.uiState = { status: 'error', mensaje: res.mensaje };
    }
    renderStaffResultados();
    // A query typed while loading is judged now that the entries are here (B3).
    programarRegistroSinResultado();
}

function renderStaffResultados() {
    const region = getStaffResultados();
    if (!region) return;
    region.innerHTML = FaqStaffResultados({
        uiState: staff.uiState,
        entradas: staff.entradas,
        consulta: staff.consulta,
        abiertas: staff.abiertas,
        reportada: staff.consulta.trim() !== '' && staff.consultaReportada === staff.consulta.trim(),
        reportando: staff.consulta.trim() !== '' && staff.consultaReportando === staff.consulta.trim(),
    });
}

/** @param {Event} e */
function handleStaffInput(e) {
    staff.consulta = /** @type {HTMLInputElement} */ (e.currentTarget).value;
    renderStaffResultados();
    programarRegistroSinResultado();
}

/** @param {KeyboardEvent} e */
function handleStaffKeydown(e) {
    if (e.key !== 'Escape' || !staff.consulta) return;
    e.preventDefault();
    const input = /** @type {HTMLInputElement} */ (e.currentTarget);
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * B3: record the query as `sin_resultado` once the employee stopped typing for
 * `FAQ_DEBOUNCE_MS`, it has 3+ characters, it shows 0 results, and it is not
 * the same key recorded last in this mount.
 */
function programarRegistroSinResultado() {
    if (staff.timer) clearTimeout(staff.timer);
    staff.timer = null;
    const consulta = staff.consulta;
    if (staff.uiState.status !== 'ready' || !esConsultaRegistrable(consulta)) return;
    if (buscar(staff.entradas, consulta).length > 0) return;

    const seq = staff.seq;
    staff.timer = setTimeout(() => {
        staff.timer = null;
        if (seq !== staff.seq || staff.consulta !== consulta) return;
        const clave = claveBusqueda(consulta);
        if (clave === staff.registradaClave) return;
        staff.registradaClave = clave;
        void registrarBusqueda(consulta, 'sin_resultado');
    }, FAQ_DEBOUNCE_MS);
}

/** @param {MouseEvent} e */
async function handleStaffClick(e) {
    const btn = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-action]'));
    if (!btn) return;
    const action = btn.dataset.action;

    if (action === 'faq-abrir') {
        const id = btn.dataset.id;
        if (staff.abiertas.has(id)) staff.abiertas.delete(id);
        else staff.abiertas.add(id);
        renderStaffResultados();
        return;
    }
    if (action === 'faq-ir-categoria') {
        document.getElementById(btn.dataset.destino ?? '')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        return;
    }
    if (action === 'faq-reintentar') {
        void cargarStaff();
        return;
    }
    if (action === 'faq-no-encontre') {
        const consulta = staff.consulta.trim();
        if (!consulta || staff.consultaReportando === consulta || staff.consultaReportada === consulta) return;
        const seq = staff.seq;
        staff.consultaReportando = consulta;
        renderStaffResultados();
        const res = await registrarBusqueda(consulta, 'no_encontre');
        if (seq !== staff.seq) return;
        // Only this report's own state: a newer report may be in flight (review resilience-f2).
        if (staff.consultaReportando === consulta) staff.consultaReportando = null;
        if (res.ok) {
            staff.consultaReportada = consulta;
        } else {
            showToast(res.mensaje, { level: 'error' });
        }
        renderStaffResultados();
    }
}

// ---------------------------------------------------------------------------
// Admin: FAQ editor
// ---------------------------------------------------------------------------

const admin = {
    seq: 0,
    uiState: /** @type {{ status: 'loading' | 'error' | 'ready', mensaje?: string }} */ ({ status: 'loading' }),
    entradas: /** @type {Array<Record<string, any>>} */ ([]),
    /**
     * The open form. `clave` identifies it: the DOM form carries it, so a redraw
     * copies typed values back only into the form they were typed in, and a save
     * that finishes late knows whether its form is still the open one.
     * Because \`renderAdmin\` copies the DOM back into \`valores\`, code that wants
     * to replace the fields must open a new form (\`abrirForm\`), not edit \`valores\`.
     * @type {null | { modo: 'crear' | 'editar', id?: string, clave: number, valores: Record<string, any>, error?: { campo: string | null, mensaje: string }, guardando?: boolean }}
     */
    form: null,
    formSeq: 0,
    confirmBorrarId: /** @type {string | null} */ (null),
    borrandoId: /** @type {string | null} */ (null),
    /** Pending search GROUPS (not rows), shown as "N búsquedas sin revisar". */
    gruposPendientes: 0,
};

/** @param {'crear' | 'editar'} modo @param {Record<string, any>} valores @param {string} [id] */
function abrirForm(modo, valores, id) {
    admin.formSeq += 1;
    admin.form = { modo, id, clave: admin.formSeq, valores };
}

const getAdminMount = () => DOM.adminContainer?.querySelector('#admin-faq-mount') ?? null;

/**
 * Teardown when leaving the tab or logging out (`SECTION_REGISTRY['faq-admin'].teardown`):
 * an action still in flight then paints, reloads and toasts success no more.
 * A failure is still toasted (`avisarSiFallo`): the admin must not leave
 * believing a delete or save worked (review resilience-f1).
 */
export function stopFaqAdmin() {
    admin.seq += 1;
}

/**
 * Entry point of the admin "FAQ" tab (`SECTION_REGISTRY['faq-admin'].mount`).
 * @param {HTMLElement | null} mount
 */
export function mountFaqAdminSection(mount) {
    if (!mount) return;
    stopFaqAdmin();
    admin.uiState = { status: 'loading' };
    admin.entradas = [];
    admin.form = null;
    admin.confirmBorrarId = null;
    admin.borrandoId = null;
    admin.gruposPendientes = 0;

    mount.innerHTML = '<div id="admin-faq-mount"></div>';
    const faqMount = getAdminMount();
    faqMount?.addEventListener('click', handleAdminClick);
    faqMount?.addEventListener('submit', handleAdminSubmit);
    renderAdmin();
    void cargarAdmin();
    void contarPendientes();
}

/**
 * Redraws the section. While a form is open, what the admin typed lives only
 * in the DOM (no input listener), so it is copied back into `admin.form.valores`
 * first — only from the DOM form that belongs to the current `admin.form` —
 * and the focused field keeps its focus and caret (review resilience-1).
 */
function renderAdmin() {
    const faqMount = getAdminMount();
    if (!faqMount) return;
    let foco = null;
    const abierto = /** @type {HTMLFormElement | null} */ (faqMount.querySelector('form[data-faq-form]'));
    if (abierto && admin.form && abierto.dataset.formClave === String(admin.form.clave)) {
        Object.assign(admin.form.valores, Object.fromEntries(new FormData(abierto).entries()));
        const activo = /** @type {HTMLInputElement | null} */ (document.activeElement);
        if (activo && abierto.contains(activo) && activo.name) {
            foco = { name: activo.name, start: activo.selectionStart, end: activo.selectionEnd };
        }
    }
    faqMount.innerHTML = FaqAdminView(admin);
    if (foco) {
        const campo = /** @type {HTMLInputElement | null} */ (faqMount.querySelector(`[name="${foco.name}"]`));
        campo?.focus();
        if (foco.start != null) campo?.setSelectionRange?.(foco.start, foco.end);
    }
}

async function cargarAdmin() {
    const seq = admin.seq;
    const res = await loadEntradas();
    if (seq !== admin.seq) return;
    if (res.ok) {
        admin.entradas = res.entradas;
        admin.uiState = { status: 'ready' };
    } else {
        admin.uiState = { status: 'error', mensaje: res.mensaje };
    }
    renderAdmin();
}

/** The pending-searches notice is a convenience: a failure only hides it. */
async function contarPendientes() {
    const seq = admin.seq;
    const res = await loadBusquedasPendientes();
    if (seq !== admin.seq) return;
    admin.gruposPendientes = res.ok ? res.grupos.length : 0;
    renderAdmin();
}

/**
 * For a response that became stale (the admin left the tab): report only a
 * failure, never paint or reload.
 * @param {{ ok: boolean, mensaje?: string }} res
 */
function avisarSiFallo(res) {
    if (!res.ok) showToast(res.mensaje, { level: 'error' });
}

/** @param {string} name */
function focusCampo(name) {
    /** @type {HTMLElement | null | undefined} */ (getAdminMount()?.querySelector(`[name="${name}"]`))?.focus();
}

/** @param {MouseEvent} e */
async function handleAdminClick(e) {
    const btn = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-action]'));
    if (!btn) return;
    const { action, id } = btn.dataset;
    const seq = admin.seq;

    if (action === 'faq-nueva') {
        abrirForm('crear', {});
        admin.confirmBorrarId = null;
        renderAdmin();
        focusCampo('pregunta');
    } else if (action === 'faq-editar') {
        const entrada = admin.entradas.find((x) => x.id === id);
        if (!entrada) return;
        abrirForm('editar', { ...entrada }, id);
        admin.confirmBorrarId = null;
        renderAdmin();
        focusCampo('pregunta');
    } else if (action === 'faq-cancelar') {
        admin.form = null;
        renderAdmin();
    } else if (action === 'faq-ocultar' || action === 'faq-mostrar') {
        const res = await cambiarVisibilidad(id, action === 'faq-mostrar');
        if (seq !== admin.seq) return avisarSiFallo(res);
        if (!res.ok) showToast(res.mensaje, { level: 'error' });
        await cargarAdmin();
    } else if (action === 'faq-borrar') {
        admin.confirmBorrarId = id;
        renderAdmin();
    } else if (action === 'faq-borrar-cancelar') {
        admin.confirmBorrarId = null;
        renderAdmin();
    } else if (action === 'faq-borrar-confirmar') {
        if (admin.borrandoId) return;
        admin.borrandoId = id;
        renderAdmin();
        const res = await eliminarEntrada(id);
        if (seq !== admin.seq) return avisarSiFallo(res);
        admin.borrandoId = null;
        admin.confirmBorrarId = null;
        if (res.ok) showToast('Pregunta borrada.', { level: 'info' });
        else showToast(res.mensaje, { level: 'error' });
        await cargarAdmin();
    } else if (action === 'faq-ir-busquedas') {
        /** @type {HTMLElement | null | undefined} */ (
            DOM.adminContainer?.querySelector('[data-action="admin-tab"][data-tab="faq-busquedas"]')
        )?.click();
    } else if (action === 'faq-reintentar') {
        admin.uiState = { status: 'loading' };
        renderAdmin();
        await cargarAdmin();
    }
}

/** @param {SubmitEvent} e */
async function handleAdminSubmit(e) {
    const form = /** @type {HTMLFormElement} */ (e.target);
    if (!form.matches('form[data-faq-form]') || !admin.form) return;
    e.preventDefault();
    if (admin.form.guardando) return;

    const valores = Object.fromEntries(new FormData(form).entries());
    const seq = admin.seq;
    admin.form = { ...admin.form, valores, error: undefined, guardando: true };
    const enviado = admin.form;
    renderAdmin();

    const res = await guardarEntrada({ ...valores, id: enviado.id });
    if (seq !== admin.seq) return avisarSiFallo(res);
    // The admin may have cancelled or opened another entry meanwhile: the
    // result only touches the form it came from (review resilience-2).
    const sigueAbierto = admin.form?.clave === enviado.clave;
    if (!res.ok) {
        if (sigueAbierto) {
            admin.form = { ...admin.form, guardando: false, error: { campo: res.kind === 'validacion' ? res.campo : null, mensaje: res.mensaje } };
            renderAdmin();
            if (res.kind === 'validacion') focusCampo(res.campo);
        } else {
            showToast(res.mensaje, { level: 'error' });
        }
        return;
    }
    if (sigueAbierto) admin.form = null;
    showToast('Pregunta guardada.', { level: 'info' });
    await cargarAdmin();
}

// ---------------------------------------------------------------------------
// Admin: Búsquedas
// ---------------------------------------------------------------------------

const busquedas = {
    seq: 0,
    uiState: /** @type {{ status: 'loading' | 'error' | 'ready', mensaje?: string }} */ ({ status: 'loading' }),
    grupos: /** @type {ReturnType<typeof import('./shared/faq-search-policy.js').agruparBusquedas>} */ ([]),
    truncado: false,
    marcando: /** @type {string | null} */ (null),
};

const getBusquedasMount = () => DOM.adminContainer?.querySelector('#admin-faq-busquedas-mount') ?? null;

/** Teardown of the Búsquedas sub-tab (`SECTION_REGISTRY['faq-busquedas'].teardown`), same rules as `stopFaqAdmin`. */
export function stopFaqBusquedas() {
    busquedas.seq += 1;
}

/**
 * Entry point of the admin "Búsquedas" sub-tab (`SECTION_REGISTRY['faq-busquedas'].mount`).
 * @param {HTMLElement | null} mount
 */
export function mountFaqBusquedasSection(mount) {
    if (!mount) return;
    stopFaqBusquedas();
    busquedas.uiState = { status: 'loading' };
    busquedas.grupos = [];
    busquedas.truncado = false;
    busquedas.marcando = null;

    mount.innerHTML = '<div id="admin-faq-busquedas-mount"></div>';
    getBusquedasMount()?.addEventListener('click', handleBusquedasClick);
    renderBusquedas();
    void cargarBusquedas();
}

function renderBusquedas() {
    const m = getBusquedasMount();
    if (!m) return;
    m.innerHTML = FaqBusquedasView(busquedas);
}

async function cargarBusquedas() {
    const seq = busquedas.seq;
    const res = await loadBusquedasPendientes();
    if (seq !== busquedas.seq) return;
    if (res.ok) {
        busquedas.grupos = res.grupos;
        busquedas.truncado = res.truncado;
        busquedas.uiState = { status: 'ready' };
    } else {
        busquedas.uiState = { status: 'error', mensaje: res.mensaje };
    }
    renderBusquedas();
}

/** @param {MouseEvent} e */
async function handleBusquedasClick(e) {
    const btn = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-action]'));
    if (!btn) return;
    const seq = busquedas.seq;

    if (btn.dataset.action === 'faq-reintentar') {
        busquedas.uiState = { status: 'loading' };
        renderBusquedas();
        await cargarBusquedas();
        return;
    }
    if (btn.dataset.action !== 'faq-marcar-revisada' || busquedas.marcando) return;
    const grupo = busquedas.grupos.find((g) => g.clave === btn.dataset.clave);
    if (!grupo) return;

    busquedas.marcando = grupo.clave;
    renderBusquedas();
    // By id: the rows loaded when the list was read. By text as well only when
    // the list was cut, to reach pending rows beyond it (review resilience-3).
    // A search recorded after the list was read stays pending and shows up on
    // the next load, which is what the admin has not seen yet.
    const res = await marcarRevisadas({ ids: grupo.ids, consultas: busquedas.truncado ? grupo.consultas : [] });
    if (seq !== busquedas.seq) return avisarSiFallo(res);
    busquedas.marcando = null;
    if (res.ok) {
        busquedas.grupos = busquedas.grupos.filter((g) => g.clave !== grupo.clave);
        showToast('Búsqueda marcada como revisada.', { level: 'info' });
        renderBusquedas();
        return;
    }
    // A failure may be partial, or the group may already be reviewed from
    // another tab: reload, so a group that is done does not stay stuck.
    showToast(res.mensaje, { level: 'error' });
    await cargarBusquedas();
}
